import { useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useUserSearch,
  useUsersTop,
  type AdminUserIdentity,
  type PlatformFilter,
} from "@/api/admin/users";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { PLATFORM_KEY, displayName, identLabel } from "./userIdentity";

/**
 * DUE-100 Phase F — Users tab. Re-keyed to `users.id` and given a platform
 * column/filter in DUE-191, since iOS guest and Apple-only accounts have no
 * `tg_id`.
 *
 * Two modes share one table:
 *   - empty query  → top list, segmented by `spent` | `credits` | `readings`
 *   - non-empty q  → search results (`/api/admin/users/search`)
 *
 * The name cell links to `/admin/users/:id` (UserDetailPage) — `users.id`
 * is the only identifier every account is guaranteed to have.
 */

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const SEARCH: CSSProperties = {
  padding: "8px 12px",
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink-2)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
};
const ROW: CSSProperties = { display: "flex", gap: 8, alignItems: "center" };
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
};
const TH: CSSProperties = {
  textAlign: "left",
  padding: "8px 6px",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)",
};
const TD: CSSProperties = {
  padding: "8px 6px",
  borderBottom: "1px solid var(--d-line)",
};
const LINK: CSSProperties = {
  color: "var(--d-violet)",
  textDecoration: "none",
};

function chipStyle(active: boolean): CSSProperties {
  return {
    padding: "4px 10px",
    borderRadius: 999,
    background: active ? "var(--d-violet)" : "transparent",
    color: active ? "var(--d-ink)" : "var(--d-violet)",
    border: "1px solid var(--d-violet)",
    fontFamily: "var(--font-body)",
    fontSize: 12,
    cursor: "pointer",
  };
}

type By = "spent" | "credits" | "readings";

// `apple_ex_guest` is deliberately not offered here — "Apple" already
// covers it, and it stays reachable only via URL/API.
const PLATFORM_OPTIONS: readonly PlatformFilter[] = [
  "all",
  "telegram",
  "ios_guest",
  "apple",
  "web",
];

/** Shared row renderer for both the top list and search results. */
function UserRow(props: {
  row: AdminUserIdentity;
  trailing: ReactNode;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { row } = props;
  const name = displayName(row);
  return (
    <tr>
      <td style={TD}>{t(PLATFORM_KEY[row.platform])}</td>
      <td style={TD}>
        <Link style={LINK} to={`/admin/users/${row.id}`}>
          {name ?? t("users.no_name")}
        </Link>
      </td>
      <td style={TD}>{identLabel(row)}</td>
      <td style={TD}>{row.username ?? "—"}</td>
      <td style={TD}>{props.trailing}</td>
    </tr>
  );
}

export function UsersPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const [by, setBy] = useState<By>("spent");
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [q, setQ] = useState("");
  const top = useUsersTop(by, platform);
  const search = useUserSearch(q, platform);

  const useSearch = q.length >= 1;
  const topRows = top.data ?? [];
  const searchRows = search.data ?? [];
  const activeQuery = useSearch ? search : top;

  if (activeQuery.isError) {
    return <AdminErrorState onRetry={() => void activeQuery.refetch()} />;
  }

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {t("users.title")}
      </h2>
      <input
        style={SEARCH}
        placeholder={t("users.search_placeholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div style={ROW}>
        <span style={{ fontSize: 12, color: "var(--d-fg-muted)" }}>
          {t("users.filter.label")}
        </span>
        {PLATFORM_OPTIONS.map((option) => (
          <button
            key={option}
            onClick={() => setPlatform(option)}
            style={chipStyle(platform === option)}
          >
            {option === "all" ? t("users.filter.all") : t(PLATFORM_KEY[option])}
          </button>
        ))}
      </div>
      {!useSearch && (
        <div style={ROW}>
          <span style={{ fontSize: 12, color: "var(--d-fg-muted)" }}>
            {t("users.top_by.label")}
          </span>
          {(["spent", "credits", "readings"] as By[]).map((option) => (
            <button
              key={option}
              onClick={() => setBy(option)}
              style={chipStyle(by === option)}
            >
              {t(`users.top_by.${option}`)}
            </button>
          ))}
        </div>
      )}
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>{t("users.col.platform")}</th>
            <th style={TH}>{t("users.col.name")}</th>
            <th style={TH}>{t("users.col.ident")}</th>
            <th style={TH}>{t("users.col.username")}</th>
            <th style={TH}>
              {useSearch ? t("users.col.locale") : t("users.col.metric")}
            </th>
          </tr>
        </thead>
        <tbody>
          {useSearch
            ? searchRows.map((r) => (
                <UserRow key={r.id} row={r} trailing={r.locale} />
              ))
            : topRows.map((r) => (
                <UserRow key={r.id} row={r} trailing={r.metric} />
              ))}
        </tbody>
      </table>
    </div>
  );
}
